//#region Utils

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region Angular Imports

import { StateService, StateProvider, StateParams } from '@uirouter/angularjs'
import * as angular from 'angular'
import 'angular-gridster'

//#endregion

//#region DevExtreme Imports

import 'devextreme/ui/button'
import 'devextreme/ui/form'
import 'devextreme/ui/load_indicator'
import 'devextreme/ui/popup'
import 'devextreme/ui/select_box'

//#endregion

import { default as apiModule, Api, LocalDictionaryFactory } from '../api/api.module'
import { default as commonModule, LoggerService } from '../common/common.module'

import { MenuItem } from '../common/sidebar-menu/common.sidebar-menu.directive'

import { IUserSession, UserSession, EventsNames, ConfigHelper, LogHelper, ConvertHelper, DataProvider, CommonHelper } from '../../helpers'

import { OpOperator } from '../../models/core'
import { Activity, Enums } from '../../models/common'
import { OpDashboard } from '../../models/custom'

import { DashboardController } from './dashboard.controller'
import { DashboardHelper } from './dashboard.helper'

//#region Asset Imports

import './dashboard.scss'
import * as TEMPLATE from './dashboard.tpl.html'

//#endregion

// Dashboard Controls

import { CardDirective } from './card/card.directive'
import { ChartDirective } from './chart/chart.directive'
import { ItemDirective } from './item/item.directive'
import { MapDirective } from './map/map.directive'
import { MeterDirective } from './meter/meter.directive'
import { SchemaDirective } from './schema/schema.directive'
import { TableDirective } from './table/table.directive'
import { TileDirective } from './tile/tile.directive'
import { DashboardCreateEditPopupComponent, DASHBOARD_CREATE_EDIT_POPUP_NAME } from './create-edit-popup/create-edit-popup.component'
import { DashboardService } from './dashboard.service';
import { DashboardDestinationLevelPopupComponent, DASHBOARD_DESTINATION_LEVEL_POPUP_NAME } from './destination-level-popup/destination-level-popup.component'
import { USER_SESSION_SERVICE_NAME, USER_SESSION_SERVICE_PROVIDER_NAME } from '../../services/user-session'

// Dashboard Controls End


// Module Consts

const CONTROLLER_NAME: string = 'DashboardController'
const MODULE_NAME: string = 'simax.dashboard'
const STATE_NAME: string = 'dashboard'
const STATE_URL: string = '/dashboard/{id}'

const LOG_PREFIX: string = '[SIMAX.Module.Dashboard]'

// Module Consts End


// Module Setup

const module = angular.module(MODULE_NAME, [commonModule, apiModule, 'gridster'])
const dashboardsLoadedDeferred: JQueryDeferred<void> = $.Deferred<void>()

module.controller('DashboardController', [
    '$scope',
    '$state',
    '$q',
    '$timeout',
    '$location',
    'api',
    'logger',
    'localDictionary',
    '$stateParams',
    USER_SESSION_SERVICE_NAME,
    'dashboardService', (
        $scope,
        $state: StateService,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        $location: ng.ILocationService,
        api: Api,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        $stateParams: StateParams,
        userSession: IUserSession,
        dashboardService: DashboardService
    ) => new DashboardController(
        $scope,
        $state,
        $q,
        $timeout,
        $location,
        api,
        logger,
        localDictionary,
        $stateParams,
        userSession,
        dashboardService)])

module.directive('itemChart', [() => new ChartDirective()])
module.directive('itemMeter', [() => new MeterDirective()])
module.directive('itemTable', [() => new TableDirective()])
module.directive('itemTile', [() => new TileDirective()])
module.directive('itemMap', [() => new MapDirective()])
module.directive('itemSchema', [() => new SchemaDirective()])
module.directive('itemCard', [() => new CardDirective()])
module.directive('item', [() => new ItemDirective()])
module.component(DASHBOARD_CREATE_EDIT_POPUP_NAME, new DashboardCreateEditPopupComponent())
module.component(DASHBOARD_DESTINATION_LEVEL_POPUP_NAME, new DashboardDestinationLevelPopupComponent())
module.factory(CommonHelper.Services.dashboard, [USER_SESSION_SERVICE_NAME, (userSession: IUserSession) => new DashboardService(userSession)])

module.config(['$stateProvider', USER_SESSION_SERVICE_PROVIDER_NAME, ($stateProvider: StateProvider, userSession: UserSession) => {
    userSession
        .getLoggedOperatorAsync()
        .then((loggedOperator: OpOperator) => {
            if (loggedOperator.hasPermission(Activity.WEBSIMAX_DASHBOARD_VIEW)) {
                setMenuItems(userSession.dataProvider, loggedOperator)
                setRouting($stateProvider)
            }
        })
}])

module.run(['$rootScope', function ($rootScope) {
    dashboardsLoadedDeferred.then(() => {
        $rootScope.$broadcast(EventsNames.REFRESH_MENU)
    })
}])

// Module Setup End


// Route Setup

function setRouting($stateProvider: StateProvider): void {
    $stateProvider.state(STATE_NAME, {
        url: STATE_URL,
        controller: CONTROLLER_NAME,
        template: TEMPLATE,
        params: {
            id: null
        }
    })
}

// Route Setup End


// Dashboard Setup

export function setMenuItems(dataProvider: DataProvider, operator: OpOperator): JQueryPromise<void> {
    return dataProvider.Operator
        .getDashboardConf(operator.idOperator)
        .then((dashboards: OpDashboard[]) => {
            const dashboardNameDict: { [index: number]: { caption: string, resource: string } } = DashboardHelper.getDashboardUniqueNames(dashboards)

            dashboards.forEach((dashboard: OpDashboard, index: number) => {
                const dashboardNames: { caption: string, resource: string } = dashboardNameDict[dashboard.idDashboard]

                const menuItemOptions: any = {
                    iconName: ConfigHelper.getIconName(ConfigHelper.Icon.DASHBOARD),
                    index: DashboardHelper.getDashboardMenuIndex(index),
                    name: STATE_NAME,
                    state: STATE_NAME,
                    resource: dashboardNames.resource,
                    caption: dashboardNames.caption,
                }

                switch (dashboard.type) {
                    default:
                    case Enums.DashboardType.Standard:
                        if (_.size(dashboards) > 1) {
                            menuItemOptions.params = {
                                id: dashboard.idDashboard.toString()
                            }
                        }

                        MenuItem.addMenuItem(
                            menuItemOptions.iconName,
                            menuItemOptions.name,
                            menuItemOptions.resource,
                            menuItemOptions.state,
                            menuItemOptions.index,
                            menuItemOptions.caption,
                            menuItemOptions.params
                        )

                        break

                    case Enums.DashboardType.DXDashboard:
                    case Enums.DashboardType.DXDashboardMulti:
                        menuItemOptions.name = 'dxdashboard'
                        menuItemOptions.state = 'dxdashboard'
                        menuItemOptions.params = {
                            id: dashboard.idDashboard.toString(),
                            index
                        }

                        MenuItem.addMenuItem(
                            menuItemOptions.iconName,
                            menuItemOptions.name,
                            menuItemOptions.resource,
                            menuItemOptions.state,
                            menuItemOptions.index,
                            menuItemOptions.caption,
                            menuItemOptions.params
                        )

                        break
                }
            })

            return dashboardsLoadedDeferred.resolve()
        })
        .fail((error: any) => {
            LogHelper.warn(LOG_PREFIX, `Unable to load dashboard configuration for idOperator: ${operator.idOperator}.`, ConvertHelper.getRequestErrorMessage(error))

            dashboardsLoadedDeferred.reject()
        })
}

// Dashboard Setup End


export default MODULE_NAME